import React, { type FC } from 'react'
import Dialog from '@mui/material/Dialog'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Grid from '@mui/material/Grid'
import fileDownload from 'js-file-download'
import { useTranslation } from 'react-i18next'

import { DialogActions, DialogContent, DialogTitle } from '@mui/material'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import { AdminDeviceProvisionsAPI } from '../../../api/admin-device-provisions-api'
import { type DeviceForProvision } from '../../../redux/admin/admin-device-provisions-types'
import {
  NewGenerateCsvFromSerialValueChangedAction,
  SetGenerateCsvFromGivenSerialDialogStateAction
} from '../../../redux/admin/admin-device-provisions-actions'
import { type RootState } from '../../../redux/store'

export const GenerateCSVFromGivenSerialDialog: FC = () => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const generateCSVDialogOpen = useAppSelector<boolean>((state: RootState) => state.adminDeviceProvisions.generateCSVDialogOpen)
  const csvExportStart = useAppSelector<string>((state: RootState) => state.adminDeviceProvisions.csvExportStart)
  const selectedTenant = useAppSelector<string>(((state: RootState) => state.adminDeviceProvisions.selectedTenant))
  const token = useAppSelector<string>(((state: RootState) => state.auth.idToken))

  const closeDialog = (): void => {
    dispatch(SetGenerateCsvFromGivenSerialDialogStateAction({ isOpen: false }))
  }

  const valueChanged = (value: string): void => {
    dispatch(NewGenerateCsvFromSerialValueChangedAction({ serialNumber: value }))
  }

  const generateCSV = (devices: DeviceForProvision[]): void => {
    const csvData = devices.map(data => [data.serialNumber, data.devEUI, data.appEUI, data.appKey])
    const joinDataAndHeader = `Serial number, DevEUI, AppEUI, AppKey \n${csvData.join('\n')}`
    // npm install js-file-download -s
    fileDownload(joinDataAndHeader, 'serials.csv')
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const fetchDataForCSV = async (serial: string) => {
    await AdminDeviceProvisionsAPI.getDevicesForProvisionByTenantAndStartingWithSerial(
      selectedTenant,
      serial,
      token
    ).then((data) => {
      if (data.succeeded) {
        generateCSV(data.payload.devices)
      } else {
        const errorKey: string = data.errorKey ?? ''

        const errMsg = errorKey !== '' ? t(errorKey) : data.message
        window.alert('Tapahtui virhe: ' + errMsg)
      }
    })
  }

  const onGenerateButtonClick = (): void => {
    fetchDataForCSV(csvExportStart).catch(err => {
      console.error('general CSV from given serial error: ', err)
    })
  }

  return (
        <Dialog
            open={generateCSVDialogOpen ?? false}
        >
            <DialogTitle>Hae ja tulosta CSV sarjanumerolla</DialogTitle>
            <DialogContent>
                <Grid item>
                    <TextField
                        fullWidth
                        id="dfp-serialNumber"
                        onChange={(event) => {
                          valueChanged(event.target.value)
                        }}
                        required
                        label="Serial number"
                        variant="standard"
                    />
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button key="cancel" color="primary" onClick={() => {
                  closeDialog()
                }}>Peruuta</Button>,
                <Button
                    key="generate"
                    onClick={onGenerateButtonClick}
                    color="secondary"
                >
                    {t('generate_csv')}
                </Button>
            </DialogActions>
        </Dialog>
  )
}
